import { useNavigate, useParams } from 'react-router-dom';
import classNames from 'classnames';

import { useGetForecast } from 'hooks/useGetForecast';
import Alert, { AlertVariant } from 'components/common/alerts/Alert';
import LocationSearch from '../locations/LocationSearch';
import CurrentConditions from './current/CurrentConditions';
import Location from './location/Location';
import ForecastDaily from './daily/ForecastDaily';
import Icon, { Icons } from 'components/common/icons/Icon';

function WeatherPage() {
  const navigate = useNavigate();
  const { lat, lon } = useParams();
  const {
    data: weather,
    isError,
    isLoading,
    isRefetching,
  } = useGetForecast(Number(lat), Number(lon));
  const alertCount = weather?.providerData?.alerts?.alert?.length || 0;
  const hasAlerts = alertCount > 0;

  if (isError) {
    return (
      <Alert variant={AlertVariant.Error} className="mx-auto max-w-sm">
        <div>Unable to retrieve the current conditions.</div>
        <div>Please refresh or try again later.</div>
      </Alert>
    );
  }

  return (
    <div className="mx-auto max-w-lg" data-testid="page-weather">
      <div className={classNames({ 'opacity-40': isRefetching })}>
        <div className="mb-8 sm:hidden">
          <LocationSearch />
        </div>

        <div className="mb-4 flex items-center justify-between">
          <Location
            isLoading={isLoading}
            location={weather?.providerData.location}
            className="grow"
          />
          <Icon
            name={Icons.TriangleExclamation}
            size="xl"
            className={classNames('ml-2 cursor-pointer text-red-800/90', { hidden: !hasAlerts })}
            onClick={() => navigate('alerts')}
            testId="page-weather-alerts-icon"
          />
          <Icon
            name={Icons.Rotate}
            spin
            size="xl"
            className={classNames('ml-2', { hidden: !isRefetching })}
            testId="page-weather-refetching-icon"
          />
        </div>

        {hasAlerts && (
          <Alert className="mb-6 cursor-pointer" onClick={() => navigate('alerts')}>
            {weather?.providerData.alerts.alert.map((alert, index) => (
              <div key={index} className="mb-1 truncate text-sm font-bold">
                {alert.headline}
              </div>
            ))}
            <div className="text-right text-sm">
              read more
              <Icon name={Icons.ArrowRight} className="ml-1" />
            </div>
          </Alert>
        )}

        <CurrentConditions
          isLoading={isLoading}
          className="mb-6"
          current={weather?.providerData.current}
          updatedAt={weather?.updatedAt}
        />

        <ForecastDaily
          className="mb-6"
          isLoading={isLoading}
          dailyForecasts={weather?.providerData.forecast.forecastday}
        />
      </div>
    </div>
  );
}

export default WeatherPage;
